import { z } from 'zod';

import { type IntegrationCapability } from '../../integrations/integration-capability/integration-capability';

/**
 * The integration capabilities the AI platform consumes (N15, N21).
 *
 * Declared as a **narrowing** of `IntegrationCapability` rather than as a fresh
 * list: the vault's capability names are the source of truth (U3), and
 * `satisfies` makes a rename over there a compile error here instead of a
 * runtime "no active provider" nobody can explain.
 *
 * The four are separate capabilities on purpose — moderation (text), text
 * similarity, face-match (image embeddings) and the photo advisory (image
 * classification, P27b1) are configured independently, and at launch only
 * Gemini offers either embedding kind while text and vision have two providers.
 * `face-embedding` takes an image where `ai-embedding` takes a string, which is
 * why it is a capability rather than a wider `embed`.
 */
export const AI_CAPABILITIES = [
  'ai-text',
  'ai-embedding',
  'face-embedding',
  'ai-image',
] as const satisfies readonly IntegrationCapability[];

export const AiCapabilitySchema = z.enum(AI_CAPABILITIES);

export type AiCapability = (typeof AI_CAPABILITIES)[number];

const CAPABILITY_SET: ReadonlySet<string> = new Set<string>(AI_CAPABILITIES);

/** Narrows a stored row's column or a route parameter. */
export function isAiCapability(value: string): value is AiCapability {
  return CAPABILITY_SET.has(value);
}
